import * as React from 'react'
import { useEffect } from 'react'
import { useUnit } from 'effector-react'
import { useDispatch } from 'react-redux'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useVerificationProfile } from './hooks'
import { useVerificationRequestsWidget } from '../atoms/duplicates/hooks'
import { CAlert, CCol } from '@coreui/react-pro'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  theme,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import Title from 'antd/lib/typography/Title'
import { RejectReasonModal } from '../widgets/VerificationHistories/components/RejectReasonModal'
import { VerificationTabs } from '../widgets/VerificationTabs'
import { VerificationClientCard } from '../widgets/VerificationClientCard'
import { VerificationComments } from '../widgets/VerificationComments'
import { ActiveFiltersDisplay } from '../widgets/ActiveFiltersDisplay'
import { FLOW_FILTER_CONFIG } from '../widgets/VerificationsListFilters'
import { useUrlFilters } from '../../../libs/useUrlFilters'
import { GoogleChatModal } from '../widgets/GoogleChatModal'
import { useAutoCheck } from '../widgets/DocumentAutoCheck/useAutoCheck'
import {
  AutoCheckHint,
  isFieldMismatch,
} from '../widgets/DocumentAutoCheck/AutoCheckHint'
import { AutoCheckBanner } from '../widgets/DocumentAutoCheck/AutoCheckBanner'
import {
  CompareFieldKey,
  CompareRow,
  valueForForm,
} from '../widgets/DocumentAutoCheck/compare'
import { Verification } from './types'
import {
  submitSaveVerificationFields,
  submitEditVerificationRequest,
  saveVerificationFields,
  submitApprove,
  approveVerification,
  $formDirtyStateFlow,
  $isFormDirtyFlow,
  setFormDirtyStateFlow,
  setIsFormDirtyFlow,
  submitSkip,
  submitExit,
} from './model'
import { verificationStatus } from '../const'
import { Guard } from '../../../features/session/guard'
import { countries } from '../../../libs/countries'

type Props = {
  className?: string
  verificationRequest: Verification
}

type FormValues = {
  firstName: string
  middleName: string
  lastName: string
  country: string
  dateOfBirth: Dayjs | null
  documentNumber: string
  expirationDate: Dayjs | null
}

const getTagColor = (status: string) => {
  switch (status) {
    case 'new':
      return 'processing'
    case 'accepted':
      return 'success'
    case 'false':
    case undefined:
    case 'rejected':
      return 'error'
    default:
      return 'processing'
  }
}

const countryOptions = countries.map(
  (item: { value: string; text: string }) => ({
    value: item.value,
    label: item.text,
  }),
)

export const VerificationRequestEditFlow = ({
  className,
  verificationRequest,
}: Props) => {
  const { token } = theme.useToken()
  const {
    clientId,
    status,
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    country,
    documentNumber,
    expirationDate,
  } = verificationRequest

  const { verificationRequest: duplicates } = useVerificationRequestsWidget(
    verificationRequest.requestId,
  )
  const duplicatesCount = duplicates?.length ?? 0

  const loadingSave = useUnit(saveVerificationFields.pending)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, dirtyFields },
  } = useForm<FormValues>({
    mode: 'onChange',
  })

  useEffect(() => {
    setIsFormDirtyFlow(isDirty)
  }, [isDirty])

  const formValues = useWatch({ control }) as FormValues
  const watchedExpirationDate = formValues.expirationDate
  const dirtyFieldsKey = Object.keys(dirtyFields)
    .filter((k) => Boolean((dirtyFields as Record<string, boolean>)[k]))
    .sort()
    .join(',')

  useEffect(() => {
    setFormDirtyStateFlow({
      dirtyFieldKeys: dirtyFieldsKey ? dirtyFieldsKey.split(',') : [],
      expirationDate:
        watchedExpirationDate && watchedExpirationDate.isValid()
          ? watchedExpirationDate.format('YYYY-MM-DD')
          : '',
      values: {
        firstName: formValues.firstName ?? '',
        middleName: formValues.middleName ?? '',
        lastName: formValues.lastName ?? '',
        country: formValues.country ?? '',
        dateOfBirth: formValues.dateOfBirth?.isValid()
          ? formValues.dateOfBirth.format('YYYY-MM-DD')
          : '',
        documentNumber: formValues.documentNumber ?? '',
        expirationDate: watchedExpirationDate?.isValid()
          ? watchedExpirationDate.format('YYYY-MM-DD')
          : '',
      },
    })
  }, [dirtyFieldsKey, formValues, watchedExpirationDate])

  useEffect(() => {
    reset({
      firstName: firstName ?? '',
      middleName: middleName ?? '',
      lastName: lastName ?? '',
      country: country ?? '',
      dateOfBirth: dateOfBirth ? dayjs(dateOfBirth) : null,
      documentNumber: documentNumber ?? '',
      expirationDate: expirationDate ? dayjs(expirationDate) : null,
    })
  }, [
    firstName,
    middleName,
    lastName,
    country,
    dateOfBirth,
    documentNumber,
    expirationDate,
    reset,
  ])

  const {
    state: autoCheckState,
    rowByKey,
    appliedKeys,
    markApplied,
  } = useAutoCheck(verificationRequest as Record<string, unknown>)

  const applyFromDoc = (row: CompareRow) => {
    const value = valueForForm(row.key, row.docRawValue)
    if (!value) return
    if (row.key === 'dateOfBirth' || row.key === 'expirationDate') {
      const d = dayjs(value)
      if (!d.isValid()) return
      setValue(row.key, d, { shouldDirty: true })
    } else {
      setValue(row.key, value, { shouldDirty: true })
    }
    markApplied(row.key)
  }

  const fieldStatus = (
    key: CompareFieldKey,
    hasError: boolean,
    isDirtyField: boolean,
  ): 'error' | 'warning' | undefined => {
    if (hasError) return 'error'
    if (isDirtyField) return 'warning'
    if (!appliedKeys.has(key) && isFieldMismatch(rowByKey.get(key)))
      return 'warning'
    return undefined
  }

  const handleFormSubmit = (data: FormValues) => {
    const updated = {
      ...verificationRequest,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      country: data.country,
      dateOfBirth: data.dateOfBirth
        ? data.dateOfBirth.format('YYYY-MM-DD')
        : '',
      documentNumber: data.documentNumber,
      expirationDate: data.expirationDate
        ? data.expirationDate.format('YYYY-MM-DD')
        : '',
    }
    submitSaveVerificationFields(updated)
  }

  const handleCancelChanges = () => {
    Modal.confirm({
      title: 'Discard changes?',
      content:
        'You have unsaved changes. Are you sure you want to discard them?',
      okText: 'Discard',
      cancelText: 'Keep editing',
      okButtonProps: { danger: true },
      onOk: () => reset(),
    })
  }

  return (
    <CCol className={className}>
      <Row gutter={16}>
        <Col span={18}>
          <VerificationTabs
            clientId={clientId}
            requestId={verificationRequest.requestId}
          />
        </Col>
        <Col span={6}>
          <Card className="mb-4">
            <Title level={2}>Verification</Title>
            {status && (
              <Tag color={getTagColor(status)} className={'mb-1'}>
                {status}
              </Tag>
            )}
            {duplicatesCount > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`Duplicates found: ${duplicatesCount}`}
                style={{ padding: '4px 8px', marginBottom: 8, fontSize: 12 }}
              />
            )}
            <Flex vertical gap="middle">
              <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Space
                  direction="vertical"
                  style={{ width: '100%' }}
                  size="middle"
                >
                  <AutoCheckBanner state={autoCheckState} />
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: 'First name is required' }}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div>
                        <label>First name</label>
                        <Input
                          {...field}
                          placeholder="First name"
                          status={fieldStatus('firstName', !!error, isDirty)}
                        />
                        {error && (
                          <div
                            style={{
                              color: '#ff4d4f',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {error.message}
                          </div>
                        )}
                        <AutoCheckHint
                          row={rowByKey.get('firstName')}
                          applied={appliedKeys.has('firstName')}
                          onApply={applyFromDoc}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="middleName"
                    control={control}
                    render={({ field, fieldState: { isDirty } }) => (
                      <div>
                        <label>Middle name</label>
                        <Input
                          {...field}
                          placeholder="Middle name"
                          status={fieldStatus('middleName', false, isDirty)}
                        />
                        <AutoCheckHint
                          row={rowByKey.get('middleName')}
                          applied={appliedKeys.has('middleName')}
                          onApply={applyFromDoc}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: 'Last name is required' }}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div>
                        <label>Last name</label>
                        <Input
                          {...field}
                          placeholder="Last name"
                          status={fieldStatus('lastName', !!error, isDirty)}
                        />
                        {error && (
                          <div
                            style={{
                              color: '#ff4d4f',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {error.message}
                          </div>
                        )}
                        <AutoCheckHint
                          row={rowByKey.get('lastName')}
                          applied={appliedKeys.has('lastName')}
                          onApply={applyFromDoc}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: 'Country is required' }}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div>
                        <label>Country</label>
                        <Select
                          {...field}
                          placeholder="Select country"
                          showSearch
                          optionFilterProp="label"
                          options={countryOptions}
                          status={fieldStatus('country', !!error, isDirty)}
                          style={{ width: '100%' }}
                        />
                        {error && (
                          <div
                            style={{
                              color: '#ff4d4f',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {error.message}
                          </div>
                        )}
                        <AutoCheckHint
                          row={rowByKey.get('country')}
                          applied={appliedKeys.has('country')}
                          onApply={applyFromDoc}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="dateOfBirth"
                    control={control}
                    rules={{ required: 'Date of birth is required' }}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div>
                        <label>Date of birth</label>
                        <DatePicker
                          {...field}
                          placeholder="YYYY-MM-DD"
                          format={{ type: 'mask', format: 'YYYY-MM-DD' }}
                          status={fieldStatus('dateOfBirth', !!error, isDirty)}
                          style={{ width: '100%' }}
                          disabledDate={(current) =>
                            current
                              ? current >
                                dayjs().subtract(18, 'year').endOf('day')
                              : false
                          }
                        />
                        {error && (
                          <div
                            style={{
                              color: '#ff4d4f',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {error.message}
                          </div>
                        )}
                        <AutoCheckHint
                          row={rowByKey.get('dateOfBirth')}
                          applied={appliedKeys.has('dateOfBirth')}
                          onApply={applyFromDoc}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="documentNumber"
                    control={control}
                    rules={{ required: 'Document number is required' }}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div>
                        <label>Document number</label>
                        <Input
                          {...field}
                          placeholder="Document number"
                          status={fieldStatus(
                            'documentNumber',
                            !!error,
                            isDirty,
                          )}
                        />
                        {error && (
                          <div
                            style={{
                              color: '#ff4d4f',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {error.message}
                          </div>
                        )}
                        <AutoCheckHint
                          row={rowByKey.get('documentNumber')}
                          applied={appliedKeys.has('documentNumber')}
                          onApply={applyFromDoc}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name="expirationDate"
                    control={control}
                    rules={{ required: 'Expiration date is required' }}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div>
                        <label>Expiration date</label>
                        <Flex gap={8}>
                          <DatePicker
                            {...field}
                            placeholder="YYYY-MM-DD"
                            format={{ type: 'mask', format: 'YYYY-MM-DD' }}
                            status={fieldStatus(
                              'expirationDate',
                              !!error,
                              isDirty,
                            )}
                            style={{ width: '100%' }}
                            disabledDate={(current) =>
                              current
                                ? current <= dayjs().startOf('day')
                                : false
                            }
                          />
                          <Button
                            style={{
                              backgroundColor: token.colorFillTertiary,
                              borderColor: token.colorBorder,
                              color: token.colorText,
                            }}
                            onClick={() =>
                              setValue(
                                'expirationDate',
                                dayjs().add(10, 'year'),
                                {
                                  shouldDirty: true,
                                },
                              )
                            }
                          >
                            Life long
                          </Button>
                        </Flex>
                        {error && (
                          <div
                            style={{
                              color: '#ff4d4f',
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {error.message}
                          </div>
                        )}
                        <AutoCheckHint
                          row={rowByKey.get('expirationDate')}
                          applied={appliedKeys.has('expirationDate')}
                          onApply={applyFromDoc}
                        />
                      </div>
                    )}
                  />

                  {isDirty && (
                    <Flex
                      vertical
                      justify="center"
                      gap={12}
                      style={{ marginTop: 16 }}
                    >
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loadingSave}
                      >
                        Save
                      </Button>
                      <Button type="text" onClick={handleCancelChanges}>
                        Cancel Changes
                      </Button>
                    </Flex>
                  )}
                </Space>
              </form>
              <VerificationComments clientId={clientId} />
            </Flex>
          </Card>
        </Col>
      </Row>
    </CCol>
  )
}

export const VerificationFlow = () => {
  const dispatch = useDispatch()
  dispatch({ type: 'set', sidebarShow: false })
  const { filters, resetFilters } = useUrlFilters(FLOW_FILTER_CONFIG)
  const { verificationRequest, error, loading } =
    useVerificationProfile(filters)
  const { requestId, status } = verificationRequest

  const handleResetFilters = () => {
    if ($isFormDirtyFlow.getState()) {
      Modal.confirm({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Continue without saving?',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: () => resetFilters(),
      })
    } else {
      resetFilters()
    }
  }
  const [isVisibleModal, setVisibleModal] = React.useState(false)
  const [isGoogleChatModalVisible, setGoogleChatModalVisible] =
    React.useState(false)
  const approveLoading = useUnit(approveVerification.pending)
  const [expirationWarning, setExpirationWarning] = React.useState<
    string | null
  >(null)

  const approveWithDuplicateCheck = (onApprove: () => void) => {
    if (verificationRequest.isDuplicate) {
      Modal.confirm({
        title: 'Duplicates found',
        content: 'You are accepting a request with duplicates. Are you sure?',
        okText: 'Yes',
        cancelText: 'Cancel',
        onOk: onApprove,
      })
    } else {
      onApprove()
    }
  }

  const handleAccept = () => {
    const dirtyState = $formDirtyStateFlow.getState()
    const expirationFromForm = dirtyState.dirtyFieldKeys.includes(
      'expirationDate',
    )
      ? dirtyState.expirationDate
      : ''
    const effectiveExpiration =
      expirationFromForm || verificationRequest.expirationDate

    if (!effectiveExpiration) {
      setExpirationWarning('You need to fill in the expiration date.')
      return
    }

    if (new Date(effectiveExpiration) < new Date()) {
      setExpirationWarning(
        'The expiration date must not be earlier than the current date.',
      )
      return
    }

    if ($isFormDirtyFlow.getState()) {
      const updated = {
        ...verificationRequest,
        ...dirtyState.values,
        expirationDate: effectiveExpiration,
      }
      approveWithDuplicateCheck(() => submitEditVerificationRequest(updated))
    } else {
      approveWithDuplicateCheck(() => submitApprove(verificationRequest))
    }
  }

  const handleReject = () => {
    if ($isFormDirtyFlow.getState()) {
      Modal.confirm({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Continue without saving?',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: () => setVisibleModal(true),
      })
    } else {
      setVisibleModal(true)
    }
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {error.message.length > 0 && (
        <CAlert color="danger">Duplicate requests found</CAlert>
      )}
      <Flex gap="large" align="baseline" wrap="wrap" justify="right">
        <Guard permission="approve_verification">
          {status !==
            verificationStatus.verificationRequestStatusAccepted() && (
            <Button
              type="primary"
              size="large"
              style={{ backgroundColor: '#2eb85c', borderColor: '#2eb85c' }}
              onClick={handleAccept}
              loading={approveLoading}
            >
              Approve
            </Button>
          )}
        </Guard>
        <Guard permission="reject_verification">
          <>
            {status !==
              verificationStatus.verificationRequestStatusRejected() && (
              <Button danger size="large" type="primary" onClick={handleReject}>
                Reject
              </Button>
            )}
          </>
        </Guard>
        <Button
          type="primary"
          size="large"
          style={{ backgroundColor: '#f9b115', borderColor: '#f9b115' }}
          onClick={() => setGoogleChatModalVisible(true)}
        >
          Send to Google Chat
        </Button>
        <Button
          type="primary"
          size="large"
          style={{ backgroundColor: '#2f54eb', borderColor: '#2f54eb' }}
          onClick={() => submitSkip(requestId)}
        >
          Skip
        </Button>
        <Button size="large" onClick={() => submitExit()}>
          Exit
        </Button>
        <RejectReasonModal
          open={isVisibleModal}
          onCancel={() => setVisibleModal(false)}
          status={verificationStatus.verificationRequestStatusRejected()}
          requestId={requestId}
        />
        <GoogleChatModal
          open={isGoogleChatModalVisible}
          onCancel={() => setGoogleChatModalVisible(false)}
          requestUrl={`${window.location.origin}/verificationsV2/edit/${requestId}`}
          clientId={verificationRequest.clientId}
          clientEmail={verificationRequest.client?.email || ''}
          clientFullName={verificationRequest.fullName || ''}
          managerEmail={verificationRequest.manager?.user?.email || ''}
        />
      </Flex>

      <ActiveFiltersDisplay filters={filters} onResetAll={handleResetFilters} />
      <VerificationClientCard
        client={{
          ...verificationRequest.client,
          fullName: verificationRequest.fullName,
        }}
        manager={verificationRequest.manager}
        className="mb-4"
        loading={loading}
        totalBalanceUSD={verificationRequest.totalBalanceUSD}
      />
      <VerificationRequestEditFlow verificationRequest={verificationRequest} />
      <Modal
        title="Warning"
        open={!!expirationWarning}
        onOk={() => setExpirationWarning(null)}
        onCancel={() => setExpirationWarning(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText="OK"
      >
        <p>{expirationWarning}</p>
      </Modal>
    </Space>
  )
}
